/**
 * Intelligent Field Selection for DataItemDetails
 * 
 * This module provides smart field selection and categorization for displaying
 * additional properties in DataItemDetails, taking advantage of the adapter pattern
 * to handle different data sources appropriately.
 */

import { ApiEndpoint } from '../types';

// Field categorization configuration constants
const FIELD_CATEGORIZATION_CONFIG = {
  /** Minimum character length to consider text as "long" and descriptive */
  MIN_LONG_TEXT_LENGTH: 20,
  /** Maximum number of high-priority fields to show in compact mode */
  MAX_COMPACT_FIELDS: 8,
  /** Maximum number of array items to display before truncation */
  MAX_ARRAY_DISPLAY_ITEMS: 5,
  /** Maximum string length before truncation with tooltip */
  MAX_STRING_DISPLAY_LENGTH: 200,
  /** Truncation preview length for long strings */
  STRING_TRUNCATION_PREVIEW_LENGTH: 197,
  /** Maximum URL display length before truncation */
  MAX_URL_DISPLAY_LENGTH: 50,
  /** URL truncation preview length */
  URL_TRUNCATION_PREVIEW_LENGTH: 47,
} as const;

/**
 * Field filtering configuration - centralized control for field visibility
 */
const FIELD_FILTERING_CONFIG = {
  /** Control flag for hiding non-useful fields from display */
  HIDE_RELATIONAL_FIELDS: true,
  /** Maximum object properties before considering it generic/non-useful */
  MAX_GENERIC_OBJECT_PROPERTIES: 1,
  /** Minimum object properties to be considered meaningful */
  MIN_MEANINGFUL_OBJECT_PROPERTIES: 2,
} as const;

export interface FieldInfo {
  key: string;
  value: any;
  displayName: string;
  category: 'details' | 'temporal' | 'technical' | 'metadata';
  priority: 'high' | 'medium' | 'low';
  dataType: 'text' | 'number' | 'boolean' | 'date' | 'array' | 'object' | 'url';
  isInteresting: boolean;
}

export interface CategorizedFields {
  details: FieldInfo[];
  temporal: FieldInfo[];
  technical: FieldInfo[];
  metadata: FieldInfo[];
}

/**
 * Simple field categorization - just show all fields with basic categorization
 */
function simpleFieldCategorization(key: string, value: any): { 
  category: FieldInfo['category'], 
  priority: FieldInfo['priority'], 
  isInteresting: boolean 
} {
  const lowerKey = key.toLowerCase();
  
  // System metadata fields (low priority but still show them)
  if (lowerKey.includes('created') || lowerKey.includes('updated') || lowerKey.includes('version') ||
      lowerKey.includes('uuid') || lowerKey.includes('guid') || lowerKey.includes('hash')) {
    return { category: 'metadata', priority: 'low', isInteresting: true };
  }
  
  // Date/time fields
  if (lowerKey.includes('date') || lowerKey.includes('time') || detectDataType(value) === 'date') {
    return { category: 'temporal', priority: 'medium', isInteresting: true };
  }
  
  // Numeric/measurement fields
  if (typeof value === 'number' || lowerKey.includes('count') || lowerKey.includes('id')) {
    return { category: 'technical', priority: 'medium', isInteresting: true };
  }
  
  // Arrays and objects (choice fields, etc.) - now go to details
  if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
    return { category: 'details', priority: 'high', isInteresting: true };
  }
  
  // Long text fields - now go to details
  if (typeof value === 'string' && value.length > FIELD_CATEGORIZATION_CONFIG.MIN_LONG_TEXT_LENGTH) {
    return { category: 'details', priority: 'high', isInteresting: true };
  }
  
  // Everything else - now goes to details
  return { category: 'details', priority: 'medium', isInteresting: true };
}

/**
 * Fields that should be excluded from display - keep this minimal
 */
const EXCLUDED_FIELDS = [
  /^(id|name|description|primary_image|thumbnail|relationships|file|url)$/i, // Basic fields shown elsewhere
  /^(fulcrum_data|django_data|form_values|raw_.*)$/i, // Raw data containers
  /^(_source|source|from_data_drawer|from data drawer|fromDataDrawer|relatedMedia)$/i, // Technical source information not useful for users
  /^(geometry|_geometry|coordinates|_coordinates|geom|location)$/i, // Spatial/geometry data not useful for users
  /^(data_source_type|datasourcetype|from_map_click|frommapclick|has_complete_data|hascompletedata)$/i, // Technical metadata not useful for users
  /^(table_name|tablename|layer_id|layerid|entity_type|entitytype|type)$/i, // System identifiers not useful for users
  /^(rel_description|relationship_data|entity_description|from_data_item_details|fromdataitemdetails)$/i, // Relationship metadata not useful for users
  /^(_relationship.*|relationship_.*|.*_relationship|.*_relationshipdata)$/i, // Generic relationship metadata patterns
];

/**
 * @deprecated Use FIELD_FILTERING_CONFIG.HIDE_RELATIONAL_FIELDS instead
 * Kept for backward compatibility
 */
const HIDE_RELATIONAL_FIELDS = FIELD_FILTERING_CONFIG.HIDE_RELATIONAL_FIELDS;

/**
 * Patterns for detecting relational field content
 */
const RELATIONAL_PATTERNS = {
  RECORD_LINK_KEYS: ['record_id'],
  PHOTO_KEYS: ['photo_id', 'caption'],
  PHOTO_FIELD_NAMES: /^(.*photos?|.*images?|.*pictures?)$/i,
  // Field names that typically contain generic objects or arrays that aren't useful to display
  GENERIC_OBJECT_FIELD_NAMES: /^(.*media|related_.*|.*_media|source|sources|from_data_drawer|fromdatadrawer)$/i,
} as const;

/**
 * Check if a value contains relational JSON patterns
 */
function containsRelationalPatterns(value: any, patterns: readonly string[]): boolean {
  if (typeof value === 'string') {
    return patterns.some(pattern => value.includes(pattern));
  }
  
  if (Array.isArray(value) || typeof value === 'object') {
    const valueStr = JSON.stringify(value);
    return patterns.some(pattern => valueStr.includes(pattern));
  }
  
  return false;
}

/**
 * Check if a field contains generic object/array data that isn't useful to display
 */
function isGenericObjectField(key: string, value: any): boolean {
  // Check for field names that typically contain generic objects
  if (RELATIONAL_PATTERNS.GENERIC_OBJECT_FIELD_NAMES.test(key)) {
    return true;
  }
  
  // Check for specific field names that should be hidden (case-insensitive)
  const lowerKey = key.toLowerCase();
  const hiddenFieldNames = [
    'source', 'from data drawer', 'fromdatadrawer', 'from_data_drawer'
  ];
  if (hiddenFieldNames.includes(lowerKey)) {
    return true;
  }
  
  // Check for arrays of objects that would display as [object Object]
  if (Array.isArray(value) && value.length > 0) {
    // If all items in array are objects (not primitives), it's likely relational data
    const allObjects = value.every(item => 
      typeof item === 'object' && 
      item !== null && 
      !Array.isArray(item)
    );
    if (allObjects) {
      return true;
    }
  }
  
  // Check for generic objects that don't have meaningful display properties
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const keys = Object.keys(value);
    
    // Skip objects with very few properties (likely not useful for display)
    if (keys.length <= FIELD_FILTERING_CONFIG.MAX_GENERIC_OBJECT_PROPERTIES) {
      return true;
    }
    
    // Skip empty objects (0 properties) - already covered by above check but kept for clarity
    if (keys.length === 0) {
      return true;
    }
    
    // Check if object has only technical/system properties
    const technicalKeys = ['id', 'uuid', 'created_at', 'updated_at', 'version', 'type', 'status'];
    const hasOnlyTechnicalKeys = keys.every(key => 
      technicalKeys.some(techKey => key.toLowerCase().includes(techKey.toLowerCase()))
    );
    
    if (hasOnlyTechnicalKeys) {
      return true;
    }
  }
  
  return false;
}

/**
 * Check if a field value contains relational data that should be hidden
 * This includes photo fields, record links, generic objects, and other non-useful data
 */
function isRelationalField(key: string, value: any): boolean {
  if (!HIDE_RELATIONAL_FIELDS) return false;
  
  // Check for generic objects/arrays that aren't useful to display
  if (isGenericObjectField(key, value)) {
    return true;
  }
  
  // Check for record link patterns
  if (containsRelationalPatterns(value, RELATIONAL_PATTERNS.RECORD_LINK_KEYS)) {
    return true;
  }
  
  // Check for photo-related patterns
  if (containsRelationalPatterns(value, RELATIONAL_PATTERNS.PHOTO_KEYS)) {
    return true;
  }
  
  // Check field name patterns for photos/images
  if (RELATIONAL_PATTERNS.PHOTO_FIELD_NAMES.test(key)) {
    if (containsRelationalPatterns(value, RELATIONAL_PATTERNS.PHOTO_KEYS)) {
      return true;
    }
  }
  
  // Check for direct object fields with relational properties
  if (typeof value === 'object' && value !== null) {
    return RELATIONAL_PATTERNS.RECORD_LINK_KEYS.some(key => key in value) ||
           RELATIONAL_PATTERNS.PHOTO_KEYS.some(key => key in value);
  }
  
  // Check for relationship metadata JSON strings
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      // Hide objects containing relationship metadata
      if (parsed.relationship_type || parsed.link_sources || parsed.spatial_intersection) {
        return true;
      }
    } catch {
      // Not JSON, continue with other checks
    }
  }
  
  return false;
}

/**
 * Detect data type of a field value
 */
function detectDataType(value: any): FieldInfo['dataType'] {
  if (value === null || value === undefined) return 'text';
  
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return 'number';
  if (Array.isArray(value)) return 'array';
  if (typeof value === 'object') return 'object';
  
  if (typeof value === 'string') {
    // Check for URL patterns
    if (/^https?:\/\//.test(value)) return 'url';
    
    // Check for date patterns
    if (/^\d{4}-\d{2}-\d{2}/.test(value) || 
        /^\d{2}\/\d{2}\/\d{4}/.test(value) ||
        !isNaN(Date.parse(value))) {
      return 'date';
    }
    
    // Check if it's a stringified number
    if (!isNaN(Number(value)) && value.trim() !== '') return 'number';
  }
  
  return 'text';
}

/**
 * Generate human-readable display name from field key
 */
function generateDisplayName(key: string): string {
  // Handle Fulcrum field IDs that start with "Field " prefix
  if (key.startsWith('Field ')) {
    const fieldId = key.substring(6); // Remove "Field " prefix
    // For short field IDs (like Fulcrum), show them as "Field ID: xxxx"
    if (fieldId.length <= 6 && /^[a-f0-9]+$/i.test(fieldId)) {
      return `Field ID: ${fieldId.toUpperCase()}`;
    }
  }
  
  return key
    .replace(/[_-]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, l => l.toUpperCase())
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Categorize and prioritize a field using simple categorization
 */
function categorizeField(key: string, value: any): Pick<FieldInfo, 'category' | 'priority' | 'isInteresting'> {
  return simpleFieldCategorization(key, value);
}

/**
 * Extract all fields from an item using completely data source agnostic approach
 * Relies on adapters to have already processed data into standard structure
 */
function extractAllFields(item: any, endpoint?: ApiEndpoint): FieldInfo[] {
  const fields: FieldInfo[] = [];
  
  // Completely generic approach: look for processed data containers first, then fallback to raw item
  let fieldSource: Record<string, any> = {};
  
  // Check for any processed data containers (adapters should put processed fields here)
  const dataContainers = Object.keys(item).filter(key => key.endsWith('_data'));
  
  if (dataContainers.length > 0) {
    // Use the first data container that has form_values or similar processed fields
    for (const containerKey of dataContainers) {
      const container = item[containerKey];
      if (container && typeof container === 'object') {
        // Look for processed field collections
        if (container.form_values && typeof container.form_values === 'object') {
          fieldSource = container.form_values;
          break;
        }
        // Could add other processed field patterns here for different adapters
        else if (container.processed_fields && typeof container.processed_fields === 'object') {
          fieldSource = container.processed_fields;
          break;
        }
      }
    }
    
    // If we found processed fields, also include relevant top-level metadata
    if (Object.keys(fieldSource).length > 0) {
      const interestingMetadata = ['status', 'created_by', 'updated_by', 'version'];
      for (const metaKey of interestingMetadata) {
        if (item[metaKey] && item[metaKey] !== 'Active' && item[metaKey] !== 'Unknown') {
          fieldSource[metaKey] = item[metaKey];
        }
      }
    }
  }
  
  // Fallback: use all item properties if no processed data containers found
  if (Object.keys(fieldSource).length === 0) {
    fieldSource = item;
  }
  
  // Process all fields using unified logic
  for (const [key, value] of Object.entries(fieldSource)) {
    // Skip excluded fields
    if (EXCLUDED_FIELDS.some(pattern => pattern.test(key))) continue;
    if (value === null || value === undefined || value === '') continue;
    if (key.startsWith('_')) continue; // System fields
    if (isRelationalField(key, value)) continue; // Skip relational fields if enabled
    
    const fieldInfo = categorizeField(key, value);
    fields.push({
      key,
      value,
      displayName: generateDisplayName(key),
      dataType: detectDataType(value),
      ...fieldInfo
    });
  }
  
  return fields;
}

/**
 * Main function to get intelligently selected and categorized fields
 * Uses adapter pattern - data source agnostic implementation
 */
export function getIntelligentFields(
  item: any, 
  endpoint?: ApiEndpoint
): CategorizedFields {
  // Data source agnostic field extraction
  const allFields = extractAllFields(item, endpoint);
  
  // Sort fields within each category by priority and then by display name
  const sortFields = (fields: FieldInfo[]) => 
    fields.sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
      return priorityDiff !== 0 ? priorityDiff : a.displayName.localeCompare(b.displayName);
    });
  
  // Categorize fields
  const categorized: CategorizedFields = {
    details: sortFields(allFields.filter(f => f.category === 'details')),
    temporal: sortFields(allFields.filter(f => f.category === 'temporal')),
    technical: sortFields(allFields.filter(f => f.category === 'technical')),
    metadata: sortFields(allFields.filter(f => f.category === 'metadata'))
  };
  
  return categorized;
}

/**
 * Get only the most interesting fields for compact display
 */
export function getHighPriorityFields(item: any, endpoint?: ApiEndpoint): FieldInfo[] {
  const categorized = getIntelligentFields(item, endpoint);
  const allFields = [
    ...categorized.details,
    ...categorized.temporal,
    ...categorized.technical,
    ...categorized.metadata
  ];
  
  return allFields
    .filter(f => f.isInteresting)
    .slice(0, FIELD_CATEGORIZATION_CONFIG.MAX_COMPACT_FIELDS);
}

/**
 * Category display configuration - centralized category metadata
 */
const CATEGORY_DISPLAY_CONFIG: Record<keyof CategorizedFields, {
  label: string;
  icon: string;
  description: string;
}> = {
  details: { 
    label: 'Details', 
    icon: 'D', 
    description: 'Key information, descriptions, and contextual details' 
  },
  temporal: { 
    label: 'Time & Dates', 
    icon: 'T', 
    description: 'When events occurred and timing information' 
  },
  technical: { 
    label: 'Measurements & Data', 
    icon: 'M', 
    description: 'Quantitative measurements and technical specifications' 
  },
  metadata: { 
    label: 'System Information', 
    icon: 'S', 
    description: 'System-generated timestamps and administrative details' 
  }
} as const;

/**
 * Get category display information
 * @param category - The field category
 * @returns Category display metadata
 */
export function getCategoryInfo(category: keyof CategorizedFields) {
  return CATEGORY_DISPLAY_CONFIG[category];
}

/**
 * Export configuration for external access and testing
 * Allows runtime configuration changes if needed
 */
export const FieldSelectionConfig = {
  FIELD_CATEGORIZATION: FIELD_CATEGORIZATION_CONFIG,
  FIELD_FILTERING: FIELD_FILTERING_CONFIG,
  CATEGORY_DISPLAY: CATEGORY_DISPLAY_CONFIG,
  RELATIONAL_PATTERNS,
  EXCLUDED_FIELDS,
} as const;